/**
 * ACRES User — Browse & submit tasks
 */
import React, { useEffect, useMemo, useState } from 'react';
import { acres } from '../../../lib/services/acresService';

interface Task {
  id: string; title: string; description?: string; instructions?: string;
  proof_requirements?: string; level: number; points: number;
  platform?: string | null; estimated_minutes?: number | null;
  cooldown_hours?: number; submissions_remaining?: number;
  cooldown_until?: string | null;
}

const PLATFORMS = ['', 'facebook','instagram','tiktok','youtube','twitter','linkedin','telegram','reddit','threads','snapchat','whatsapp'];
const LEVELS = [0, 1, 2, 3, 4, 5];

export const AcresBrowseTasks: React.FC<{ user?: any }> = () => {
  const [tasks, setTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filterPlat, setFilterPlat] = useState('');
  const [filterLvl, setFilterLvl] = useState(0);
  const [activeTask, setActiveTask] = useState<Task | null>(null);
  const [proofText, setProofText] = useState('');
  const [proofFile, setProofFile] = useState<File | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const load = async () => {
    setLoading(true); setError(null);
    try { const r = await acres.listTasks(); setTasks(r.tasks || []); }
    catch (e: any) { setError(e?.message || 'Failed to load tasks'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const visible = useMemo(() => tasks.filter(t =>
    (!filterPlat || t.platform === filterPlat) &&
    (!filterLvl || t.level === filterLvl)
  ), [tasks, filterPlat, filterLvl]);

  const openTask = (t: Task) => { setActiveTask(t); setProofText(''); setProofFile(null); };

  const fileToBase64 = (f: File): Promise<string> => new Promise((res, rej) => {
    const r = new FileReader();
    r.onload = () => res(String(r.result || '').split(',').pop() || '');
    r.onerror = rej; r.readAsDataURL(f);
  });

  const submit = async () => {
    if (!activeTask) return;
    if (!proofText && !proofFile) { alert('Provide proof text or upload a file'); return; }
    setSubmitting(true);
    try {
      const payload: any = {
        template_id: activeTask.id,
        proof_text: proofText || undefined,
        idempotency_key: `sub_${activeTask.id}_${Date.now()}_${Math.random().toString(36).slice(2,8)}`
      };
      if (proofFile) {
        if (proofFile.size > 10 * 1024 * 1024) throw new Error('File too large (max 10 MB)');
        payload.proof_file = {
          name: proofFile.name,
          mime: proofFile.type,
          size: proofFile.size,
          data_base64: await fileToBase64(proofFile)
        };
      }
      await acres.submitTask(payload);
      setActiveTask(null);
      alert('Submitted! A moderator will review your proof shortly.');
      await load();
    } catch (e: any) { alert(e?.message || 'Submission failed'); }
    finally { setSubmitting(false); }
  };

  return (
    <div>
      {error && <div style={errBox}>{error}</div>}
      <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
        <select value={filterPlat} onChange={e => setFilterPlat(e.target.value)} style={input}>
          {PLATFORMS.map(p => <option key={p} value={p}>{p ? `Platform: ${p}` : 'All platforms'}</option>)}
        </select>
        <select value={filterLvl} onChange={e => setFilterLvl(Number(e.target.value))} style={input}>
          {LEVELS.map(l => <option key={l} value={l}>{l ? `Level ${l}` : 'All levels'}</option>)}
        </select>
        <button onClick={load} style={btnGhost}>↻ Refresh</button>
      </div>

      {loading && <div style={{ color: '#9CA3AF' }}>Loading…</div>}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 12 }}>
        {visible.map(t => {
          const onCooldown = t.cooldown_until && new Date(t.cooldown_until) > new Date();
          const noSlots = t.submissions_remaining === 0;
          const disabled = onCooldown || noSlots;
          return (
            <div key={t.id} style={{ ...card, opacity: disabled ? 0.6 : 1 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
                <div style={{ fontWeight: 700, fontSize: 14 }}>{t.title}</div>
                <div style={{ background: '#F59E0B', color: '#000', padding: '2px 8px', borderRadius: 4, fontSize: 12, fontWeight: 700 }}>+{t.points}</div>
              </div>
              <div style={{ fontSize: 11, color: '#9CA3AF', marginTop: 4 }}>L{t.level} · {t.platform || 'any'} · ~{t.estimated_minutes || '?'} min</div>
              {t.description && <div style={{ fontSize: 12, color: '#D1D5DB', marginTop: 6 }}>{t.description}</div>}
              <button disabled={!!disabled} onClick={() => openTask(t)} style={{ ...btnPrimary, marginTop: 10, width: '100%', opacity: disabled ? 0.5 : 1, cursor: disabled ? 'not-allowed' : 'pointer' }}>
                {onCooldown ? `Cooldown until ${new Date(t.cooldown_until!).toLocaleString()}` : noSlots ? 'No slots left' : 'Submit Proof'}
              </button>
            </div>
          );
        })}
        {!visible.length && !loading && <div style={{ color: '#9CA3AF', padding: 16 }}>No tasks match your filters right now.</div>}
      </div>

      {activeTask && (
        <div style={overlay}>
          <div style={modal}>
            <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 4 }}>{activeTask.title}</div>
            <div style={{ fontSize: 12, color: '#9CA3AF', marginBottom: 10 }}>L{activeTask.level} · {activeTask.platform || 'any'} · +{activeTask.points} pts</div>
            {activeTask.instructions && <div style={hint}><b>How to do this:</b><br />{activeTask.instructions}</div>}
            {activeTask.proof_requirements && <div style={hint}><b>Required proof:</b><br />{activeTask.proof_requirements}</div>}
            <div style={{ marginTop: 10 }}>
              <div style={{ fontSize: 12, color: '#9CA3AF', marginBottom: 4 }}>Proof description (optional but recommended)</div>
              <textarea value={proofText} onChange={e => setProofText(e.target.value)} placeholder="Describe what you did, share the URL of your post, etc." style={{ ...input, minHeight: 80 }} />
            </div>
            <div style={{ marginTop: 10 }}>
              <div style={{ fontSize: 12, color: '#9CA3AF', marginBottom: 4 }}>Upload proof (image, video, or PDF — max 10MB)</div>
              <input type="file" accept="image/*,video/*,application/pdf" onChange={e => setProofFile(e.target.files?.[0] || null)} style={input} />
              {proofFile && <div style={{ fontSize: 11, color: '#9CA3AF', marginTop: 4 }}>{proofFile.name} · {(proofFile.size / 1024).toFixed(0)} KB</div>}
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 14 }}>
              <button onClick={() => setActiveTask(null)} style={btnGhost} disabled={submitting}>Cancel</button>
              <button onClick={submit} style={btnPrimary} disabled={submitting}>{submitting ? 'Submitting…' : 'Submit for Review'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const card: React.CSSProperties = { background: '#0F0F0F', border: '1px solid #1E1E1E', borderRadius: 8, padding: 12 };
const input: React.CSSProperties = { padding: '8px 10px', background: '#0F0F0F', border: '1px solid #2A2A2A', borderRadius: 6, color: '#FFF', fontSize: 13, width: '100%' };
const btnPrimary: React.CSSProperties = { padding: '8px 14px', background: '#F59E0B', color: '#000', border: 'none', borderRadius: 6, fontWeight: 700, cursor: 'pointer', fontSize: 13 };
const btnGhost: React.CSSProperties = { padding: '8px 14px', background: 'transparent', color: '#E5E7EB', border: '1px solid #374151', borderRadius: 6, cursor: 'pointer' };
const overlay: React.CSSProperties = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 };
const modal: React.CSSProperties = { background: '#121212', border: '1px solid #2A2A2A', borderRadius: 12, padding: 20, width: 'min(620px, 100%)', maxHeight: '90vh', overflowY: 'auto' };
const hint: React.CSSProperties = { padding: 8, background: '#0A0A0A', border: '1px solid #1E1E1E', borderRadius: 6, fontSize: 12, color: '#D1D5DB', marginBottom: 6 };
const errBox: React.CSSProperties = { padding: 10, background: 'rgba(220,38,38,0.1)', border: '1px solid #DC2626', borderRadius: 6, color: '#FCA5A5', marginBottom: 10 };
